import i18next from 'i18next';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { createSegment } from 'common/api/v1/segmentsLayout';
import {
    solarCharacterization as defaultSolarCharacterization,
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
} from 'common/components/maps/constants';
import {
    getPolygonHeadings,
    setCeilZone,
} from 'common/components/segment/asceHelpers';
import {
    POLYGON_MAP,
    POLYGON_OBSTACLE,
    POLYGON_RECTANGLE,
    POLYGON_SEGMENT,
    POLYGON_CIRCLE,
} from 'common/components/segment/constants';
import {
    buildGroups,
    checkCallback,
    checkCallbackError,
    formatSegment,
    getModulesGroups,
    markGroupEdges,
    prepareModulesGroups,
} from 'common/components/segment/helpers';
import {
    getSegmentArea,
    getSegmentDiameter,
    showReponseErrorsAsAlert,
} from 'common/utils/helpers';
import {
    getFrameSpacingViaSystemUid,
    getMinCol,
    getMinMaxCols,
    getMinMaxGroupTilt,
    getMinMaxRows,
    getMinRow,
    getOrientationViaSystemUid,
    getSpaceBetweenModules,
} from 'common/utils/helpers/systemsValues';
import showToast from 'common/utils/showToast';

import {
    SAVE_SEGMENT,
    SAVE_SEGMENT_FAILURE,
    SAVE_SEGMENT_SUCCESS,
} from '../actionTypes';
import {
    detectFieldChanges,
    getNewModules,
    getObstaceZIndex,
} from '../helpers';
import { panelLayoutV2Actions } from '../reducer';
import * as selectors from '../selectors';

import handleCollition from './handleCollition';
import prepareEditSegment from './prepareEditSegment';
import selectSegment from './selectSegment';
import setNewSegmentConfig from './setNewSegmentConfig';
import updateSegments from './updateSegments';

const buildSegmentPayload = ({
    config,
    drawingType,
    newSegmentConfig,
    parentFieldSegment,
    presetConfiguration,
    segments,
}) => {
    const { proposal } = parentFieldSegment;
    const { panel, system } = proposal;
    const systemId = !isEmpty(system) ? system.id : newSegmentConfig?.system;
    const { commercialOfferId, google, mapRef, polygon } = config;
    const mapValue = mapRef.current.self();

    let defaultOfferPanelValues = {
        assigned: 0,
        color: SEGMENT_COLOR,
        id: null,
        quantity: 0,
        stroke_color: SEGMENT_STROKE_COLOR,
    };
    let buildingHeading = null;
    let ceilZonedGroups = [];
    let groups = [];
    let module = null;

    if (panel) {
        module = panel;
        defaultOfferPanelValues = {
            ...defaultOfferPanelValues,
            ...module,
        };
    }

    const { assigned, color, id, quantity, stroke_color } =
        defaultOfferPanelValues;

    const obstacles = segments.filter((segment) =>
        [POLYGON_OBSTACLE, POLYGON_CIRCLE].includes(segment.type),
    );

    const {
        building_height: height = 0,
        category,
        preset_configuration,
        quantity: newSegmentConfigQuantity = null,
        sloof_type,
        system_uid,
    } = newSegmentConfig;

    let newQuantity = quantity;
    let azimuth = defaultSolarCharacterization.azimuth;
    const fill_algorithm = newSegmentConfig.fill_algorithm || 'fill';

    if (newSegmentConfigQuantity) newQuantity = newSegmentConfigQuantity;
    if (fill_algorithm === 'fill') newQuantity = Infinity;

    if (drawingType === POLYGON_RECTANGLE) {
        const polygonHeadings = getPolygonHeadings({
            google,
            polygonCoords: polygon,
        });
        buildingHeading = polygonHeadings[2]?.heading || 0;
        azimuth = buildingHeading;
    }

    const ceil_tilt = newSegmentConfig.ceil_tilt || 0;
    const orientation = getOrientationViaSystemUid(system_uid);
    const minCols = getMinCol(system_uid);
    const minRows = getMinRow(system_uid);
    const [tilt] = getMinMaxGroupTilt(system_uid);
    const { frame_row_spacing, frame_col_spacing } =
        getFrameSpacingViaSystemUid(system_uid);
    const { col_spacing, row_spacing } = getSpaceBetweenModules(system_uid);

    const solarCharacterization = {
        assigned,
        height: height || 0,
        polygon: JSON.stringify(polygon),
        quantity: newQuantity,
        ...defaultSolarCharacterization,
        azimuth,
        ceil_tilt,
        col_spacing,
        frame_col_spacing,
        frame_cols_number: minCols,
        frame_row_spacing,
        frame_rows_number: minRows,
        orientation,
        row_spacing,
        tilt,
    };

    const [, maxCols] = getMinMaxCols(system_uid, panel.width);
    const [, maxRows] = getMinMaxRows(system_uid);
    const algorithm = 'dynamic';

    const { boxes, grouped } = getNewModules({
        algorithm,
        building_heading: buildingHeading,
        data: {
            ...solarCharacterization,
            minRows,
            minCols: algorithm === 'dynamic' ? 1 : minCols,
            maxRows,
            maxCols,
        },
        google,
        map: mapValue,
        module,
        obstacles,
        sloof_type,
    });

    let newGrouped = grouped;

    if (fill_algorithm === 'fill') {
        const newBoxes = boxes.map((box) => ({ ...box, enable: true }));
        newGrouped = buildGroups(newBoxes);
    }

    const modules_groups = getModulesGroups(newGrouped);
    groups = prepareModulesGroups(modules_groups);

    if (drawingType === POLYGON_RECTANGLE) {
        const markedGroupEdges = markGroupEdges(modules_groups);
        ceilZonedGroups = setCeilZone({
            buildingHeight: height,
            ceilTilt: ceil_tilt,
            google,
            groups: markedGroupEdges,
            heading: buildingHeading,
            polygon,
            sloofType: sloof_type,
        });
    }

    const solar_modules = boxes.map((module) => ({
        cell: module.cell,
        col: module.col,
        group: module.group,
        is_enable: fill_algorithm === 'fill' ? true : module.enable,
        row: module.row,
        solar_module_points: JSON.stringify(
            module.paths.map((point) => ({
                x: point[1],
                y: point[0],
            })),
        ),
    }));

    const countSegments = segments.filter((segment) =>
        [POLYGON_SEGMENT, POLYGON_RECTANGLE].includes(segment.type),
    ).length;

    const field_segments = [
        {
            name: `${i18next.t('Segment')} ${countSegments + 1}`,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
            solar_modules,
        },
    ];

    const solution_fields = {
        beam_spacing: newSegmentConfig.beam_spacing,
        front_base_height: newSegmentConfig.front_base_height,
        lfoot_supports_width: newSegmentConfig.lfoot_supports_width,
        outer_rail_length: newSegmentConfig.outer_rail_length,
        plate_support_length: newSegmentConfig.plate_support_length,
        rail_spacing: newSegmentConfig.rail_spacing,
        rail_support_length: newSegmentConfig.rail_support_length,
        seam_distance: newSegmentConfig.seam_distance,
        sheet_gauge: newSegmentConfig.sheet_gauge,
        support_length: newSegmentConfig.support_length,
    };

    const { hasChanges = false } = detectFieldChanges(
        presetConfiguration,
        solution_fields,
    );

    const additionalCustomInputs = () => {
        if (preset_configuration && !hasChanges)
            return { preset_configuration };
        return { ...solution_fields };
    };

    const payload = {
        asce_metadata: ceilZonedGroups,
        category: category || null,
        commercial_offer: commercialOfferId,
        custom_inputs: {
            algorithm,
            building_heading: buildingHeading,
            building_height: newSegmentConfig.building_height,
            category,
            ceil_tilt: newSegmentConfig.ceil_tilt,
            drawing_type: drawingType,
            fill_algorithm: newSegmentConfig.fill_algorithm,
            gclamp_type: newSegmentConfig.gclamp_type,
            panel_orientation: orientation,
            sloof_type: newSegmentConfig.sloof_type,
            system_uid: newSegmentConfig.system_uid,
            tilt,
            type_application: newSegmentConfig.type_application,
            ...additionalCustomInputs(),
        },
        field_segments,
        groups,
        parent_field_segment: parentFieldSegment.id,
        segment_color: color,
        segment_stroke_color: stroke_color,
        solar_module: id,
        system: systemId,
        system_uid: system_uid || null,
        total_area: getSegmentArea(google, polygon),
        type: POLYGON_SEGMENT,
        zIndex: 0,
        ...solarCharacterization,
    };

    return payload;
};

const buildObstaclePayload = ({
    config,
    parentFieldSegment,
    segments,
    drawingType,
}) => {
    const { commercialOfferId, google, polygon } = config;

    const zIndex = getObstaceZIndex({
        google,
        polygon,
        segments,
        selectedSegment: { id: null },
    });

    const countSegments = segments.filter(() =>
        [POLYGON_OBSTACLE, POLYGON_CIRCLE].includes(drawingType),
    ).length;

    const field_segments = [
        {
            name: `${i18next.t('Obstacle')} ${countSegments + 1}`,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
        },
    ];
    let custom_inputs = { drawing_type: drawingType };

    let diameter = 0;
    if (drawingType === POLYGON_CIRCLE) {
        diameter = getSegmentDiameter(google, polygon);
        custom_inputs = { ...custom_inputs, diameter };
    }

    const payload = {
        commercial_offer: commercialOfferId,
        custom_inputs: custom_inputs,
        field_segments,
        height: 0,
        parent_field_segment: parentFieldSegment.id,
        safe_zone: 0,
        segment_color: '#8c8c8c',
        segment_stroke_color: '#11EFD9',
        solar_module: null,
        total_area: getSegmentArea(google, polygon),
        type: POLYGON_OBSTACLE,
        zindex: zIndex,
    };

    return payload;
};

const buildImagePayload = ({ config, parentFieldSegment, segments }) => {
    const { commercialOfferId, google, polygon } = config;

    const countSegments = segments.filter(
        (segment) => segment.type === POLYGON_MAP,
    ).length;

    const field_segments = [
        {
            name: `${i18next.t('Image')} ${countSegments + 1}`,
            polygon: polygon.map((point) => ({
                x_coordinate: point[1],
                y_coordinate: point[0],
            })),
        },
    ];

    const payload = {
        commercial_offer: commercialOfferId,
        custom_inputs: {},
        field_segments,
        height: 0,
        opacity: 100,
        parent_field_segment: parentFieldSegment.id,
        rotate: 0,
        segment_color: '#8c8c8c',
        segment_stroke_color: '#11EFD9',
        solar_module: null,
        total_area: getSegmentArea(google, polygon),
        type: POLYGON_MAP,
    };

    return payload;
};

export default (config) => (dispatch, getState) => {
    const { commercialOfferId, google, mapRef } = config;
    const state = getState();
    const drawingType = selectors.getDrawingType(state);
    const parentFieldSegment = selectors.getParentFieldSegmentData(state);
    const newSegmentConfig = selectors.getNewSegmentConfig(state);
    const presetConfiguration = selectors.getPresetConfiguration(state);

    const segments = selectors.getSegmentsData(state);
    const mapValue = mapRef.current.self();

    let payload = null;

    if ([POLYGON_RECTANGLE, POLYGON_SEGMENT].includes(drawingType)) {
        payload = buildSegmentPayload({
            config,
            drawingType,
            newSegmentConfig,
            parentFieldSegment,
            presetConfiguration,
            segments,
        });
    }

    if ([POLYGON_OBSTACLE, POLYGON_CIRCLE].includes(drawingType)) {
        payload = buildObstaclePayload({
            config,
            drawingType,
            parentFieldSegment,
            segments,
        });
    }

    if (drawingType === POLYGON_MAP) {
        payload = buildImagePayload({ config, parentFieldSegment, segments });
    }

    dispatch(panelLayoutV2Actions[SAVE_SEGMENT]());

    createSegment(payload)
        .then((response) => {
            const segmentData = get(response, 'data', {});
            const formatedSegment = formatSegment(
                segmentData,
                parentFieldSegment.id,
            );
            const newSegment = formatedSegment;
            dispatch(
                panelLayoutV2Actions[SAVE_SEGMENT_SUCCESS](formatedSegment),
            );

            const newValuesSegments = [...segments, newSegment];
            const updatedSegments = newValuesSegments.map((segment) => ({
                ...segment,
                open: newSegment.id === segment.id,
            }));

            dispatch(updateSegments(updatedSegments));

            dispatch(selectSegment(newSegment));

            dispatch(prepareEditSegment(newSegment.id, google));

            dispatch(setNewSegmentConfig({}));

            showToast({ body: i18next.t('The segment has been saved') });

            if ([POLYGON_OBSTACLE, POLYGON_CIRCLE].includes(drawingType)) {
                dispatch(
                    handleCollition({
                        callback: (id, modules) =>
                            checkCallback(id, modules, mapRef),
                        callbackError: (id, values) =>
                            checkCallbackError(id, values),
                        commercialOfferId,
                        google,
                        mapValue,
                        obstacle: newSegment,
                    }),
                );
            }
        })
        .catch((error) => {
            dispatch(
                panelLayoutV2Actions[SAVE_SEGMENT_FAILURE](
                    error?.response?.data?.errors,
                ),
            );
            showReponseErrorsAsAlert(dispatch, error.response);
        });
};
